<template>
  <div class="card" :class="{'mb-5': !collapsed, 'mb-2 show-border': collapsed}">
    <div class="card-header" v-if="!editing_list">
      <div class="list-header">
        <i class="list-type-icon ion" :class="content_type_icons[list.content_type]" :title="content_type_names[list.content_type]"></i>
        <i v-if="!list.public" class="fa fa-user-secret private-icon" title="This list is private. Only you can view and edit it."></i>
        <h3 class="list-title mb-1" v-if="header_link">
          <router-link :to="{name: 'listdetail', params: {id: list.id}}">{{ list.name }}</router-link>
        </h3>
        <h3 class="list-title mb-1" v-else>{{ list.name }}</h3>
        <div class="list-header-right">
          <a href="#" @click.prevent="editList">
            <i class="list-type-icon ion ion-md-settings edit-list-button"></i>
          </a>
        </div>
      </div>
      <div v-if="details" class="mt-1">
        <p>{{ itemCount }} item{{ itemCount | pluralize }} <br>
          <router-link :to="{name: 'profile', params: {username: list.creator.username}}">{{ list.creator.username }}</router-link>
        </p>
      </div>
    </div>
    <div v-else class="p-3 edit-list-form">
      <div v-if="confirm_delete">
        <p>Delete this list forever?</p>
        <div>
          <button class="btn btn-danger" @click.prevent="deleteList">Delete List</button>
          <button class="btn btn-success" @click.prevent="cancelEditList">Nah, let's keep it</button>
        </div>
      </div>
      <div v-else>
        <div class="edit-menu" v-if="editable">
          <!-- private edit menu -->
          <input
            type="text"
            class="form-control edit-list-name"
            v-model="edit_name"
            @keyup.enter="saveEditList">
          <div class="form-check text-left my-3">
            <label class="form-check-label">
              <input class="form-check-input" type="checkbox" checked="" v-model="edit_public">
              <span class="form-check-sign"></span>
              Public List: anyone can view, but only you can edit
            </label>
          </div>
        </div>
        <div class="list-tools my-2">
          <!-- public list options menu -->
          <button class="btn btn-warning btn-sm" @click="copyList">Make a copy of this list</button>
        </div>
        <div class="d-flex align-items-center" :class="{'justify-content-between': editable, 'justify-content-around': !editable}">
          <a href="#" class="text-warning" @click.prevent="cancelEditList">Cancel</a>
          <a href="#" v-if="editable" class="text-danger p-1" @click.prevent="confirm_delete = true"><i class="fa fa-trash"></i> Delete List</a>
          <a href="#" v-if="editable" class="btn btn-sm btn-success" @click.prevent="saveEditList">Save</a>
        </div>
      </div>
    </div>
    <div class="card-body" :class="{'collapsed': collapsed}">
      <draggable
        class="list-group"
        tag="ul"
        v-bind="dragOptions"
        v-model="listItems"
        handle=".handle"
        @start="drag = true"
        @end="drag = false"
        v-if="!collapsed"
      >
        <li
          v-for="item in listItems"
          :key="item.id"
          class="list-group-item"
          :class="{'editing': item.id && editing_item.id === item.id, 'has-thumbnail': !!item.thumbnail, 'has-link': !!item.link}"
        >
          <template v-if="item.id && editing_item.id === item.id">
            <!-- edit form -->
            <input
              type="text"
              :ref="'edit_item'"
              class="form-control"
              v-model="editing_item.content"
              @keyup.enter="updateItem"
              @keyup.escape="clearEdit"
            >
            <input
              type="text"
              class="form-control my-1"
              placeholder="link"
              :ref="'edit_item'"
              v-show="edit_details"
              v-model="editing_item.link"
              @keyup.enter="updateItem"
              @keyup.escape="clearEdit"
            >
            <input
              type="text"
              class="form-control my-1"
              placeholder="details"
              :ref="'edit_item'"
              v-show="edit_details"
              v-model="editing_item.details"
              @keyup.enter="updateItem"
              @keyup.escape="clearEdit"
            >
            <div class="row">
              <div class="col-8">
                <input
                  type="text"
                  class="form-control my-1"
                  placeholder="Image URL"
                  :ref="'edit_item'"
                  v-show="edit_details"
                  v-model="editing_item.thumbnail"
                  @keyup.enter="updateItem"
                  @keyup.escape="clearEdit"
                >
              </div>
              <div class="col-4">
                <input
                  type="text"
                  class="form-control my-1"
                  placeholder="Year"
                  :ref="'edit_item'"
                  v-show="edit_details"
                  v-model="editing_item.year"
                  @keyup.enter="updateItem"
                  @keyup.escape="clearEdit"
                >
              </div>
            </div>
            <div class="d-flex justify-content-between input-options w-100">
              <div>
                <button class="btn btn-link btn-primary short" @click.prevent.stop="clearEdit">Cancel</button>
              </div>
              <div>
                <button class="btn btn-link btn-info short" @click.prevent.stop="editDetails" v-if="!edit_details">
                  <i class="ion ion-md-add"></i>
                  Edit Details
                </button>
              </div>
              <div>
                <button class="btn btn-link btn-success short" @click.prevent.stop="updateItem">Save</button>
              </div>
            </div>
          </template>
          <list-item-content
            v-else
            :item="item"
            :editable="editable"
            @edit="editItem(item)"
            @delete="deleteItem(item)"
            :menu="menu"
          ></list-item-content>
        </li>
        <li v-if="editable" class="list-group-item edit-button" :class="{'adding': adding}" @click="showAddItemForm">
          <i class="fa fa-plus" v-if="!adding"></i>
          <template v-if="!isRichContent">
            <input
              type="text"
              class="form-control"
              placeholder="text"
              ref="list_input"
              v-show="adding"
              v-model="adding_item.content"
              @keyup.enter="saveNewItem"
              @keyup.escape="clearAdd"
            >
            <input
              type="text"
              class="form-control my-1"
              placeholder="link"
              ref="link_input"
              v-show="add_details"
              v-model="adding_item.link"
              @keyup.enter="saveNewItem"
              @keyup.escape="clearAdd"
            >
            <input
              type="text"
              class="form-control my-1"
              placeholder="details"
              v-show="add_details"
              v-model="adding_item.details"
              @keyup.enter="saveNewItem"
              @keyup.escape="clearAdd"
            >
            <div class="row">
              <div class="col-8">
                <input
                  type="text"
                  class="form-control my-1"
                  placeholder="Image URL"
                  v-show="add_details"
                  v-model="adding_item.thumbnail"
                  @keyup.enter="saveNewItem"
                  @keyup.escape="clearAdd"
                >
              </div>
              <div class="col-4">
                <input
                  type="text"
                  class="form-control my-1"
                  placeholder="Year"
                  v-show="add_details"
                  v-model="adding_item.year"
                  @keyup.enter="saveNewItem"
                  @keyup.escape="clearAdd"
                >
              </div>
            </div>
          </template>
          <listwise-select
            ref="richSelect"
            :type="list.content_type"
            v-show="adding && isRichContent"
            @add-selected="addSelectItem"
          ></listwise-select>
          <div v-if="adding" class="d-flex justify-content-between input-options">
            <div>
              <a href="#" @click.prevent.stop="clearAdd" class="px-2">Cancel</a>
            </div>
            <div>
              <button class="btn btn-link btn-info short" @click.prevent.stop="addDetails" v-if="!add_details">
                <i class="ion ion-md-add"></i>
                Add Details
              </button>
            </div>
            <div>
              <button class="btn btn-link btn-success short" @click.prevent.stop="saveNewItem">Save</button>
            </div>
          </div>
        </li>
      </draggable>
      <div v-if="limit !== 0 && itemCount > limit && !collapsed">
        <router-link :to="{name: 'listdetail', params: {id: list.id}}">And {{ itemCount - limit }} more</router-link>
      </div>
    </div>
  </div>
</template>
<script>

import _ from 'lodash'
import draggable from "vuedraggable"

import ListwiseSelect from '@/components/ListwiseSelect'
import ListItemContent from '@/components/ListItemContent'
import constants from '@/constants'

export default {

  name: 'ListwiseList',

  props: {
    list: Object,
    editable: Boolean,
    header_link: Boolean,
    details: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    limit: {
      type: Number,
      default: 0
    },
    menu: {
      type: Boolean,
      default: false
    }
  },

  components: {
    draggable,
    ListItemContent,
    ListwiseSelect,
  },

  data () {
    return {
      adding: false,
      add_details: false,
      adding_item: {content: '', link: ''},
      editing_item: {},
      edit_details: false,
      expanded_descriptions: [],
      content_type_icons: constants.content_type_icons,
      content_type_names: constants.content_type_names,
      editing_list: false,
      edit_name: '',
      edit_public: true,
      confirm_delete: false
    }
  },

  computed: {
    listItems: {
      get () {
        if (this.limit === 0) {
          return this.list.items
        }
        return this.list.items.slice(0, this.limit)
      },
      set (items) {
        this.saveList(items)
      }
    },
    itemCount () {
      if (!this.list.items) return ''
      return this.list.items.length
    },
    dragOptions() {
      return {
        animation: 200,
        group: "description",
        disabled: false,
        ghostClass: "ghost"
      };
    },
    isRichContent () {
      return constants.rich_content_types.includes(this.list.content_type)
    }
  },
  methods: {
    addSelectItem (item) {
      this.adding_item = item
      this.saveNewItem()
    },
    showAddItemForm() {
      if (!this.adding) {
        this.adding = true
        this.$nextTick(() => {
          if (this.isRichContent) {
            this.$refs.richSelect.focus()
          } else {
            this.$refs.list_input ? this.$refs.list_input.focus():null}
          }
        )
      }
    },
    clearAdd() {
      this.adding_item = {content: '', link: ''};
      this.adding = false
      this.add_details = false
    },
    clearEdit(){
      this.editing_item = {}
      this.edit_details = false
    },
    addDetails () {
      this.add_details = true
    },
    saveList (items) {
      let new_items = _.map(_.clone(items), (item, idx) => {
        item.order = idx + 1
        return item
      })
      this.$store.dispatch('lists/saveList', {...this.list, items: new_items})
    },
    saveNewItem () {
      let new_items = _.clone(this.list.items)
      new_items.push(this.adding_item)
      this.saveList(new_items)
      this.adding_item = {content: '', link: ''};
    },
    editItem (item) {
      this.editing_item = _.clone(item)
      this.$nextTick(() => this.$refs['edit_item'][0]? this.$refs['edit_item'][0].focus():null)
    },
    editDetails () {
      this.edit_details = true
    },
    updateItem () {
      this.$store.dispatch('lists/editItem', {list_id: this.list.id, item: this.editing_item})
      this.editing_item = {}
    },
    deleteItem (item) {
      this.$store.dispatch('lists/deleteItem', {list_id: this.list.id, item_id: item.id})
    },
    toggleDetails (item_id) {
      if (this.expanded_descriptions.includes(item_id)) {
        this.expanded_descriptions = this.expanded_descriptions.filter(i => i !== item_id)
      } else {
        this.expanded_descriptions.push(item_id)
      }
    },
    editList () {
      this.editing_list = true
      this.edit_name = this.list.name
      this.edit_public = this.list.public
    },
    cancelEditList () {
      this.editing_list = false
      this.edit_name = ''
      this.edit_public = true
      this.confirm_delete = false
    },
    deleteList () {
      this.$store.dispatch('lists/deleteList', this.list)
      this.$router.push({ name: 'profile', params: { username: this.list.creator.username } })
    },
    saveEditList () {
      let edited_list = _.clone(this.list)
      edited_list.name = this.edit_name
      edited_list.public = this.edit_public
      this.$store.dispatch('lists/editList', edited_list)
      this.editing_list = false
    },
    copyList () {
      this.$store.dispatch('lists/copyList', this.list).then(resp => {
        this.$router.push({ name: 'listdetail', params: { id: resp.data.id }});
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.list-header {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  .list-title {
    display: inline-block;
    margin: 0 auto;
  }
  .list-type-icon {
    color: #5b60c5;
    display: inline-block;
    font-size: 24px;
    line-height: 30px;
    padding: 0px 7px;
    float: left;
  }
  .private-icon {
    color: #5b60c5;
    display: inline-block;
    font-size: 18px;
    line-height: 20px;
    float: left;
  }
  .list-header-right {
    width: 35px;
    .edit-list-button:hover {
      color: #7276ce;
    }
  }
}
.list-group-item {
  border-top: none;
  border-right: none;
  border-left: none;
  border-bottom: 0.0625rem solid rgba(43, 43, 45, 0.65);
  margin-bottom: 0;
  padding: 0;
  &:hover, &:focus {
    background-color: #222225;
  }
  &.adding, &.editing {
    .input-options {
      & > * {
        width: 100px;
      }
    }
    padding: .5rem .5rem 0rem .5rem;
    input.form-contol {
      padding: 1.5rem 0.7rem;
    }
    .btn-link.short {
      padding: 0rem 0.7rem;
    }
  }
  &.editing {
    display: flex;
    flex-direction: column;
  }
  &.edit-button.adding:hover {
    background: #202022;
  }
  &.edit-button:not(.adding) {
    padding: 1rem;
    border-bottom: 0.125rem solid rgba(29, 140, 248, 0.5);
    &:hover {
      cursor: pointer;
      background-color: #2c2c2f;
    }
  }
}
.list-tools-controls {
  padding: 0;
  .tools-menu-button {
    font-size: 24px;
    line-height: 24px;
    background: none;
    border: none;
    cursor: pointer;
    color: #5b60c5;
    margin-right: 3px;
    &:focus {
      outline: none;
    }
    &:hover {
      color: #7276ce;
    }
  }
}

.edit-list-form {
  .edit-list-name {
    font-size: 1.4375rem;
  }
}

.card {
  &.show-border {
    border: 1px solid #356886;
  }
  .card-body{
    &.show-toolbar {
      padding: 15px 15px 0px;
    }
    &.collapsed {
      padding: 5px;
    }
  }
}

@media screen and (max-width: 768px) {
  .card {
    .card-header {
      padding: 10px 10px 0;
    }
    .card-body {
      padding: 10px;
      &.show-toolbar {
        padding: 15px 5px 0px;
      }
    }
  }
}
</style>
